import React, { useState, useEffect, useRef } from 'react';
import './StaffFormModal.css';
import { fetchProtectedObjectUrl } from '../utils/authFetch';

export interface Staff {
  id: string;
  name: string;
  status: 'Hoạt động' | 'Đã nghỉ';
  role?: string | undefined;
  color?: string | undefined;
  email?: string | undefined;
  username?: string | undefined;
  password?: string | undefined;
  so_dien_thoai?: string | undefined;
  employee_type?: 'FULL-TIME' | 'PART-TIME' | string | undefined;
  basic_salary?: number | undefined;
  extra_salary?: number | undefined;
  full_time_salary?: number | undefined;
  allowance?: number | undefined;
  bank_id?: string | undefined;
  bank_name?: string | undefined;
  bank_account?: string | undefined;
  so_cccd?: string | undefined;
  gioi_tinh?: string | undefined;
  ngay_sinh?: string | undefined;
  que_quan?: string | undefined;
  noi_thuong_tru?: string | undefined;
  ngay_cap_cccd?: string | undefined;
  noi_cap_cccd?: string | undefined;
  dac_diem_nhan_dang?: string | undefined;
  ngay_het_han_cccd?: string | undefined;
  cccd_mat_truoc_path?: string | undefined;
  cccd_mat_sau_path?: string | undefined;
  probation_start_date?: string | undefined;
  official_start_date?: string | undefined;
  safebook_employee_code?: string | undefined;
  safebook_employee_name?: string | undefined;
  department_code?: string | undefined;
  department_name?: string | undefined;
}

export interface BankItem {
  id: string;
  bank_id: number;
  name: string;
  code: string;
  short_name: string;
  bin: string;
  logo?: string;
}

interface StaffFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  staff: Staff | null; // null => Thêm nhân viên mới; Staff object => Chỉnh sửa nhân viên
  onSave: (data: Partial<Staff>) => Promise<void>;
  onDelete?: (staffId: string) => Promise<void>;
  banks: BankItem[];
}

export const StaffFormModal: React.FC<StaffFormModalProps> = ({
  isOpen,
  onClose,
  staff,
  onSave,
  onDelete,
  banks
}) => {
  const isEdit = !!staff;

  // Form State
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [soDienThoai, setSoDienThoai] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [employeeType, setEmployeeType] = useState<'FULL-TIME' | 'PART-TIME'>('FULL-TIME');
  const [fullTimeSalary, setFullTimeSalary] = useState<number | ''>('');
  const [allowance, setAllowance] = useState<number | ''>('');
  const [basicSalary, setBasicSalary] = useState<number | ''>('');
  const [extraSalary, setExtraSalary] = useState<number | ''>('');
  const [bankId, setBankId] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [status, setStatus] = useState<'Hoạt động' | 'Đã nghỉ'>('Hoạt động');
  const [color, setColor] = useState('#8d2a2c');

  // Dates
  const [probationStartDate, setProbationStartDate] = useState('');
  const [officialStartDate, setOfficialStartDate] = useState('');

  // CCCD Info
  const [soCccd, setSoCccd] = useState('');
  const [gioiTinh, setGioiTinh] = useState('Nam');
  const [ngaySinh, setNgaySinh] = useState('');
  const [ngayCapCccd, setNgayCapCccd] = useState('');
  const [ngayHetHanCccd, setNgayHetHanCccd] = useState('');
  const [queQuan, setQueQuan] = useState('');
  const [noiThuongTru, setNoiThuongTru] = useState('');
  const [noiCapCccd, setNoiCapCccd] = useState('');
  const [dacDiemNhanDang, setDacDiemNhanDang] = useState('');

  // CCCD Photos
  const [frontFile, setFrontFile] = useState<File | null>(null);
  const [backFile, setBackFile] = useState<File | null>(null);
  const [frontPreview, setFrontPreview] = useState<string | null>(null);
  const [backPreview, setBackPreview] = useState<string | null>(null);
  const [cccdMatTruocPath, setCccdMatTruocPath] = useState<string | null>(null);
  const [cccdMatSauPath, setCccdMatSauPath] = useState<string | null>(null);

  // Safebooks Alias
  const [safebookEmployeeCode, setSafebookEmployeeCode] = useState('');
  const [safebookEmployeeName, setSafebookEmployeeName] = useState('');
  const [departmentCode, setDepartmentCode] = useState('');
  const [departmentName, setDepartmentName] = useState('');

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [scanMessage, setScanMessage] = useState<string | null>(null);

  const frontInputRef = useRef<HTMLInputElement | null>(null);
  const backInputRef = useRef<HTMLInputElement | null>(null);

  // Load initial staff data when modal opens
  useEffect(() => {
    if (!isOpen) return;

    setFormError(null);
    setScanMessage(null);
    setShowPassword(false);
    setFrontFile(null);
    setBackFile(null);

    if (staff) {
      // Populate edit mode
      setName(staff.name || '');
      setUsername(staff.username || '');
      setPassword(staff.password || '');
      setSoDienThoai(staff.so_dien_thoai || '');
      setEmail(staff.email || '');
      setRole(staff.role || '');
      setEmployeeType(staff.employee_type === 'PART-TIME' ? 'PART-TIME' : 'FULL-TIME');
      setFullTimeSalary(staff.full_time_salary ?? '');
      setAllowance(staff.allowance ?? '');
      setBasicSalary(staff.basic_salary ?? '');
      setExtraSalary(staff.extra_salary ?? '');
      setBankId(staff.bank_id ? String(staff.bank_id) : '');
      setBankAccount(staff.bank_account || '');
      setStatus(staff.status || 'Hoạt động');
      setColor(staff.color || '#8d2a2c');

      // Dates (format YYYY-MM-DD for <input type="date">)
      setProbationStartDate(staff.probation_start_date ? staff.probation_start_date.split('T')[0] : '');
      setOfficialStartDate(staff.official_start_date ? staff.official_start_date.split('T')[0] : '');

      setSoCccd(staff.so_cccd || '');
      setGioiTinh(staff.gioi_tinh || 'Nam');
      setNgaySinh(staff.ngay_sinh ? staff.ngay_sinh.split('T')[0] : '');
      setNgayCapCccd(staff.ngay_cap_cccd ? staff.ngay_cap_cccd.split('T')[0] : '');
      setNgayHetHanCccd(staff.ngay_het_han_cccd ? staff.ngay_het_han_cccd.split('T')[0] : '');
      setQueQuan(staff.que_quan || '');
      setNoiThuongTru(staff.noi_thuong_tru || '');
      setNoiCapCccd(staff.noi_cap_cccd || '');
      setDacDiemNhanDang(staff.dac_diem_nhan_dang || '');

      setCccdMatTruocPath(staff.cccd_mat_truoc_path || null);
      setCccdMatSauPath(staff.cccd_mat_sau_path || null);
      // Ảnh CCCD cần đăng nhập nên tải qua fetch (có token) rồi hiển thị bằng object URL.
      setFrontPreview(null);
      setBackPreview(null);
      if (staff.cccd_mat_truoc_path) {
        void fetchProtectedObjectUrl(`/api/staff/media/${staff.cccd_mat_truoc_path}`).then(setFrontPreview);
      }
      if (staff.cccd_mat_sau_path) {
        void fetchProtectedObjectUrl(`/api/staff/media/${staff.cccd_mat_sau_path}`).then(setBackPreview);
      }

      setSafebookEmployeeCode(staff.safebook_employee_code || '');
      setSafebookEmployeeName(staff.safebook_employee_name || staff.name || '');
      setDepartmentCode(staff.department_code || '');
      setDepartmentName(staff.department_name || '');
    } else {
      // Populate create mode defaults
      setName('');
      setUsername('');
      setPassword('');
      setSoDienThoai('');
      setEmail('');
      setRole('CSKH');
      setEmployeeType('FULL-TIME');
      setFullTimeSalary('');
      setAllowance('');
      setBasicSalary('');
      setExtraSalary('');
      setBankId('');
      setBankAccount('');
      setStatus('Hoạt động');
      setColor('#8d2a2c');

      setProbationStartDate('');
      setOfficialStartDate('');

      setSoCccd('');
      setGioiTinh('Nam');
      setNgaySinh('');
      setNgayCapCccd('');
      setNgayHetHanCccd('');
      setQueQuan('');
      setNoiThuongTru('');
      setNoiCapCccd('');
      setDacDiemNhanDang('');

      setCccdMatTruocPath(null);
      setCccdMatSauPath(null);
      setFrontPreview(null);
      setBackPreview(null);

      setSafebookEmployeeCode('');
      setSafebookEmployeeName('');
      setDepartmentCode('');
      setDepartmentName('');
    }
  }, [isOpen, staff]);

  if (!isOpen) return null;

  // Handle image files selection
  const handleFrontFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFrontFile(file);
      setFrontPreview(URL.createObjectURL(file));
    }
  };

  const handleBackFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setBackFile(file);
      setBackPreview(URL.createObjectURL(file));
    }
  };

  // AI Scan CCCD
  const handleScanCccd = async () => {
    if (!frontFile && !backFile) {
      setFormError('Vui lòng chọn ít nhất ảnh mặt trước hoặc mặt sau CCCD để scan');
      return;
    }

    try {
      setIsScanning(true);
      setFormError(null);
      setScanMessage('Đang phân tích hình ảnh CCCD bằng AI (Ollama Vision)...');

      const formData = new FormData();
      if (frontFile) formData.append('mat_truoc', frontFile);
      if (backFile) formData.append('mat_sau', backFile);
      if (staff?.id) formData.append('ma_nhan_vien', staff.id);

      const res = await fetch('/api/staff/parse-cccd', {
        method: 'POST',
        body: formData
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Lỗi khi scan CCCD');
      }

      const parsed = data.data.parsed || {};
      if (parsed.so_cccd) setSoCccd(parsed.so_cccd);
      if (parsed.ho_ten && !name) setName(parsed.ho_ten);
      if (parsed.gioi_tinh) setGioiTinh(parsed.gioi_tinh === 'Nữ' ? 'Nữ' : 'Nam');
      if (parsed.ngay_sinh) setNgaySinh(parsed.ngay_sinh);
      if (parsed.que_quan) setQueQuan(parsed.que_quan);
      if (parsed.noi_thuong_tru) setNoiThuongTru(parsed.noi_thuong_tru);
      if (parsed.ngay_cap_cccd) setNgayCapCccd(parsed.ngay_cap_cccd);
      if (parsed.noi_cap_cccd) setNoiCapCccd(parsed.noi_cap_cccd);
      if (parsed.dac_diem_nhan_dang) setDacDiemNhanDang(parsed.dac_diem_nhan_dang);
      if (parsed.ngay_het_han_cccd) setNgayHetHanCccd(parsed.ngay_het_han_cccd);

      if (data.data.cccd_mat_truoc_path) setCccdMatTruocPath(data.data.cccd_mat_truoc_path);
      if (data.data.cccd_mat_sau_path) setCccdMatSauPath(data.data.cccd_mat_sau_path);

      setScanMessage('Scan CCCD thành công! Các thông tin đã được tự động điền vào form.');
    } catch (err: any) {
      console.error('Error scanning CCCD:', err);
      setFormError(err.message || 'Lỗi khi scan CCCD');
      setScanMessage(null);
    } finally {
      setIsScanning(false);
    }
  };

  // Submit Handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setFormError('Vui lòng nhập tên nhân viên');
      return;
    }

    try {
      setIsSubmitting(true);
      setFormError(null);

      // Upload files if new files were selected
      let finalFrontPath = cccdMatTruocPath;
      let finalBackPath = cccdMatSauPath;

      if (frontFile || backFile) {
        const uploadFormData = new FormData();
        if (frontFile) uploadFormData.append('mat_truoc', frontFile);
        if (backFile) uploadFormData.append('mat_sau', backFile);
        if (staff?.id) uploadFormData.append('ma_nhan_vien', staff.id);

        const uploadRes = await fetch('/api/staff/upload-cccd', {
          method: 'POST',
          body: uploadFormData
        });

        const uploadData = await uploadRes.json();
        if (uploadData.success) {
          if (uploadData.data.cccd_mat_truoc_path) finalFrontPath = uploadData.data.cccd_mat_truoc_path;
          if (uploadData.data.cccd_mat_sau_path) finalBackPath = uploadData.data.cccd_mat_sau_path;
        }
      }

      // Find bank name from bankId
      const selectedBank = banks.find(b => String(b.id) === String(bankId) || String(b.bank_id) === String(bankId));
      const bankName = selectedBank ? (selectedBank.short_name || selectedBank.name) : undefined;

      const payload: Partial<Staff> = {
        name: name.trim(),
        username: username.trim() || undefined,
        password: password.trim() || undefined,
        so_dien_thoai: soDienThoai.trim() || undefined,
        email: email.trim() || undefined,
        role: role.trim() || undefined,
        color: color || '#8d2a2c',
        status,
        employee_type: employeeType,
        full_time_salary: fullTimeSalary === '' ? 0 : Number(fullTimeSalary),
        allowance: allowance === '' ? 0 : Number(allowance),
        basic_salary: basicSalary === '' ? 0 : Number(basicSalary),
        extra_salary: extraSalary === '' ? 0 : Number(extraSalary),
        bank_id: bankId || undefined,
        bank_name: bankName,
        bank_account: bankAccount.trim() || undefined,
        probation_start_date: probationStartDate || undefined,
        official_start_date: officialStartDate || undefined,
        so_cccd: soCccd.trim() || undefined,
        gioi_tinh: gioiTinh || undefined,
        ngay_sinh: ngaySinh || undefined,
        que_quan: queQuan.trim() || undefined,
        noi_thuong_tru: noiThuongTru.trim() || undefined,
        ngay_cap_cccd: ngayCapCccd || undefined,
        noi_cap_cccd: noiCapCccd.trim() || undefined,
        dac_diem_nhan_dang: dacDiemNhanDang.trim() || undefined,
        ngay_het_han_cccd: ngayHetHanCccd || undefined,
        cccd_mat_truoc_path: finalFrontPath || undefined,
        cccd_mat_sau_path: finalBackPath || undefined,
        safebook_employee_code: safebookEmployeeCode.trim() || undefined,
        safebook_employee_name: safebookEmployeeName.trim() || name.trim(),
        department_code: departmentCode.trim() || undefined,
        department_name: departmentName.trim() || undefined
      };

      await onSave(payload);
      onClose();
    } catch (err: any) {
      console.error('Error saving staff:', err);
      setFormError(err.message || 'Lỗi khi lưu thông tin nhân viên');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="staff-modal-overlay" onClick={onClose}>
      <div className="staff-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="staff-modal-header">
          <div className="staff-modal-title">
            <i className={isEdit ? "fas fa-user-edit" : "fas fa-user-plus"}></i>
            <span>{isEdit ? `Chỉnh sửa nhân viên: ${staff?.name || ''}` : 'Thêm Nhân Viên Mới'}</span>
          </div>
          <button type="button" className="staff-modal-close" onClick={onClose} aria-label="Đóng">
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Form Error or Scan Notice */}
        {formError && (
          <div className="staff-modal-alert alert-danger">
            <i className="fas fa-exclamation-circle"></i>
            <span>{formError}</span>
          </div>
        )}
        {scanMessage && (
          <div className="staff-modal-alert alert-success">
            <i className="fas fa-check-circle"></i>
            <span>{scanMessage}</span>
          </div>
        )}

        {/* Modal Body: 2-Column Form */}
        <form onSubmit={handleSubmit} className="staff-modal-form">
          <div className="staff-modal-grid">
            {/* LEFT COLUMN: Account & Job Details */}
            <div className="staff-modal-col left-col">
              <div className="form-field-group">
                <label className="form-label required">Tên nhân viên</label>
                <div className="input-with-icon">
                  <i className="fas fa-user input-icon"></i>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Nhập tên nhân viên..."
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="form-row-2">
                <div className="form-field-group">
                  <label className="form-label">Username</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Nhập username..."
                    className="form-input"
                  />
                </div>
                <div className="form-field-group">
                  <label className="form-label">
                    {isEdit ? "Mật khẩu mới (để trống nếu không đổi)" : "Mật khẩu"}
                  </label>
                  <div className="input-with-action">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={isEdit ? "Đổi mật khẩu..." : "Nhập mật khẩu..."}
                      className="form-input"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      title={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                    >
                      <i className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"}></i>
                    </button>
                  </div>
                </div>
              </div>

              {employeeType === 'FULL-TIME' && (
                <div className="form-row-2">
                  <div className="form-field-group">
                    <label className="form-label">Ngày bắt đầu thử việc</label>
                    <input
                      type="date"
                      value={probationStartDate}
                      onChange={(e) => setProbationStartDate(e.target.value)}
                      className="form-input"
                    />
                  </div>
                  <div className="form-field-group">
                    <label className="form-label">Ngày làm chính thức</label>
                    <input
                      type="date"
                      value={officialStartDate}
                      onChange={(e) => setOfficialStartDate(e.target.value)}
                      className="form-input"
                    />
                  </div>
                </div>
              )}

              <div className="form-row-2">
                <div className="form-field-group">
                  <label className="form-label">Vai trò / Role</label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Chọn vai trò...</option>
                    <option value="CSKH">CSKH (Chăm sóc khách hàng)</option>
                    <option value="SX_AO">SX_AO (Sản xuất áo)</option>
                    <option value="SX_THE">SX_THE (Sản xuất thẻ)</option>
                    <option value="ADMIN">ADMIN (Quản trị viên)</option>
                  </select>
                </div>
                <div className="form-field-group">
                  <label className="form-label required">Loại nhân viên</label>
                  <div className="segmented-btn-group">
                    <button
                      type="button"
                      className={`segmented-btn ${employeeType === 'FULL-TIME' ? 'active' : ''}`}
                      onClick={() => setEmployeeType('FULL-TIME')}
                    >
                      FULL-TIME
                    </button>
                    <button
                      type="button"
                      className={`segmented-btn ${employeeType === 'PART-TIME' ? 'active' : ''}`}
                      onClick={() => setEmployeeType('PART-TIME')}
                    >
                      PART-TIME
                    </button>
                  </div>
                </div>
              </div>

              <div className="form-row-2">
                <div className="form-field-group">
                  <label className="form-label">Lương Full-time/tháng (VND)</label>
                  <input
                    type="number"
                    value={fullTimeSalary}
                    onChange={(e) => setFullTimeSalary(e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="VD: 8000000"
                    className="form-input"
                    min={0}
                  />
                </div>
                <div className="form-field-group">
                  <label className="form-label">Trợ cấp (VND)</label>
                  <input
                    type="number"
                    value={allowance}
                    onChange={(e) => setAllowance(e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="VD: 4000000"
                    className="form-input"
                    min={0}
                  />
                </div>
              </div>

              <div className="form-row-2">
                <div className="form-field-group">
                  <label className="form-label">Lương Part-time/giờ (VND)</label>
                  <input
                    type="number"
                    value={basicSalary}
                    onChange={(e) => setBasicSalary(e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="0"
                    className="form-input"
                    min={0}
                  />
                </div>
                <div className="form-field-group">
                  <label className="form-label">Cộng thêm tăng ca/giờ (VND)</label>
                  <input
                    type="number"
                    value={extraSalary}
                    onChange={(e) => setExtraSalary(e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="0"
                    className="form-input"
                    min={0}
                  />
                </div>
              </div>

              <div className="form-row-2">
                <div className="form-field-group">
                  <label className="form-label">Ngân hàng</label>
                  <select
                    value={bankId}
                    onChange={(e) => setBankId(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Chọn ngân hàng...</option>
                    {banks.map((b) => (
                      <option key={b.id || b.bank_id} value={String(b.id || b.bank_id)}>
                        {b.short_name || b.name} ({b.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-field-group">
                  <label className="form-label">Số tài khoản</label>
                  <input
                    type="text"
                    value={bankAccount}
                    onChange={(e) => setBankAccount(e.target.value)}
                    placeholder="Nhập số tài khoản ngân hàng..."
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-row-2">
                <div className="form-field-group">
                  <label className="form-label">Số điện thoại</label>
                  <input
                    type="tel"
                    value={soDienThoai}
                    onChange={(e) => setSoDienThoai(e.target.value)}
                    placeholder="Nhập số điện thoại..."
                    className="form-input"
                  />
                </div>
                <div className="form-field-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Nhập email nhân viên..."
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-row-2">
                <div className="form-field-group">
                  <label className="form-label">Trạng thái</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as 'Hoạt động' | 'Đã nghỉ')}
                    className="form-select"
                  >
                    <option value="Hoạt động">Hoạt động</option>
                    <option value="Đã nghỉ">Đã nghỉ</option>
                  </select>
                </div>
                <div className="form-field-group">
                  <label className="form-label">Màu hiển thị</label>
                  <div className="color-picker-row">
                    <input
                      type="color"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      className="color-input"
                    />
                    <input
                      type="text"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      className="form-input hex-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: CCCD & Safebooks */}
            <div className="staff-modal-col right-col">
              <div className="section-divider">
                <i className="fas fa-id-card"></i>
                <span>Căn cước công dân</span>
              </div>

              {/* Upload 2 mặt CCCD */}
              <div className="cccd-cards-grid">
                <div className="cccd-card">
                  <div className="cccd-card-header">
                    <span>Mặt trước CCCD</span>
                  </div>
                  <div className="cccd-preview-area">
                    {frontPreview ? (
                      <img src={frontPreview} alt="Mặt trước CCCD" className="cccd-img-preview" />
                    ) : (
                      <div className="cccd-placeholder">
                        <i className="far fa-image"></i>
                        <span>Chưa có ảnh</span>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={frontInputRef}
                    onChange={handleFrontFileChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="btn-upload-cccd"
                    onClick={() => frontInputRef.current?.click()}
                  >
                    <i className="fas fa-upload"></i>
                    <span>{frontPreview ? 'Thay ảnh mặt trước' : 'Chọn ảnh mặt trước'}</span>
                  </button>
                </div>

                <div className="cccd-card">
                  <div className="cccd-card-header">
                    <span>Mặt sau CCCD</span>
                  </div>
                  <div className="cccd-preview-area">
                    {backPreview ? (
                      <img src={backPreview} alt="Mặt sau CCCD" className="cccd-img-preview" />
                    ) : (
                      <div className="cccd-placeholder">
                        <i className="far fa-image"></i>
                        <span>Chưa có ảnh</span>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={backInputRef}
                    onChange={handleBackFileChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="btn-upload-cccd"
                    onClick={() => backInputRef.current?.click()}
                  >
                    <i className="fas fa-upload"></i>
                    <span>{backPreview ? 'Thay ảnh mặt sau' : 'Chọn ảnh mặt sau'}</span>
                  </button>
                </div>
              </div>

              {/* Scan CCCD Button */}
              <button
                type="button"
                className="btn-ai-scan-cccd"
                onClick={handleScanCccd}
                disabled={isScanning || (!frontFile && !backFile && !frontPreview)}
                title="Bóc tách thông tin tự động bằng AI từ ảnh CCCD"
              >
                <i className={isScanning ? "fas fa-spinner fa-spin" : "fas fa-qrcode"}></i>
                <span>{isScanning ? "Đang scan CCCD bằng AI..." : "Scan CCCD"}</span>
              </button>

              <div className="form-row-2">
                <div className="form-field-group">
                  <label className="form-label">Số CCCD</label>
                  <div className="input-with-icon">
                    <i className="far fa-id-badge input-icon"></i>
                    <input
                      type="text"
                      value={soCccd}
                      onChange={(e) => setSoCccd(e.target.value)}
                      placeholder="Số căn cước 12 số..."
                      className="form-input"
                    />
                  </div>
                </div>
                <div className="form-field-group">
                  <label className="form-label">Giới tính</label>
                  <select
                    value={gioiTinh}
                    onChange={(e) => setGioiTinh(e.target.value)}
                    className="form-select"
                  >
                    <option value="Nam">Nam</option>
                    <option value="Nữ">Nữ</option>
                  </select>
                </div>
              </div>

              <div className="form-row-3">
                <div className="form-field-group">
                  <label className="form-label">Ngày sinh</label>
                  <input
                    type="date"
                    value={ngaySinh}
                    onChange={(e) => setNgaySinh(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-field-group">
                  <label className="form-label">Ngày cấp</label>
                  <input
                    type="date"
                    value={ngayCapCccd}
                    onChange={(e) => setNgayCapCccd(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-field-group">
                  <label className="form-label">Hạn sử dụng</label>
                  <input
                    type="date"
                    value={ngayHetHanCccd}
                    onChange={(e) => setNgayHetHanCccd(e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-field-group">
                <label className="form-label">Quê quán</label>
                <textarea
                  rows={2}
                  value={queQuan}
                  onChange={(e) => setQueQuan(e.target.value)}
                  placeholder="Quê quán trên CCCD..."
                  className="form-textarea"
                />
              </div>

              <div className="form-field-group">
                <label className="form-label">Nơi thường trú</label>
                <textarea
                  rows={2}
                  value={noiThuongTru}
                  onChange={(e) => setNoiThuongTru(e.target.value)}
                  placeholder="Nơi thường trú trên CCCD..."
                  className="form-textarea"
                />
              </div>

              <div className="form-row-2">
                <div className="form-field-group">
                  <label className="form-label">Nơi cấp CCCD</label>
                  <input
                    type="text"
                    value={noiCapCccd}
                    onChange={(e) => setNoiCapCccd(e.target.value)}
                    placeholder="Đơn vị cấp CCCD..."
                    className="form-input"
                  />
                </div>
                <div className="form-field-group">
                  <label className="form-label">Đặc điểm nhận dạng</label>
                  <input
                    type="text"
                    value={dacDiemNhanDang}
                    onChange={(e) => setDacDiemNhanDang(e.target.value)}
                    placeholder="Vết sẹo / Nốt ruồi..."
                    className="form-input"
                  />
                </div>
              </div>

              {/* Safebooks Config Section */}
              <div className="section-divider safebooks-divider">
                <i className="fas fa-cogs"></i>
                <span>Cấu hình Safebooks (Alias)</span>
              </div>

              <div className="form-row-2">
                <div className="form-field-group">
                  <label className="form-label">Mã Safebooks</label>
                  <input
                    type="text"
                    value={safebookEmployeeCode}
                    onChange={(e) => setSafebookEmployeeCode(e.target.value)}
                    placeholder="VD: NV041, BYDUNINV008..."
                    className="form-input"
                  />
                </div>
                <div className="form-field-group">
                  <label className="form-label">Tên trên Safebooks</label>
                  <input
                    type="text"
                    value={safebookEmployeeName}
                    onChange={(e) => setSafebookEmployeeName(e.target.value)}
                    placeholder="Mặc định dùng Tên nhân viên"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-row-2">
                <div className="form-field-group">
                  <label className="form-label">Mã bộ phận</label>
                  <input
                    type="text"
                    value={departmentCode}
                    onChange={(e) => setDepartmentCode(e.target.value)}
                    placeholder="VD: PKT, CSKH, PSX-CHUNG..."
                    className="form-input"
                  />
                </div>
                <div className="form-field-group">
                  <label className="form-label">Tên bộ phận</label>
                  <input
                    type="text"
                    value={departmentName}
                    onChange={(e) => setDepartmentName(e.target.value)}
                    placeholder="VD: KẾ TOÁN, Đóng gói..."
                    className="form-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer Actions */}
          <div className="staff-modal-footer">
            <div className="footer-left">
              {isEdit && onDelete && (
                <button
                  type="button"
                  className="btn-modal-danger"
                  onClick={() => staff?.id && onDelete(staff.id)}
                  disabled={isSubmitting}
                >
                  <i className="fas fa-trash"></i>
                  <span>Xóa nhân viên</span>
                </button>
              )}
            </div>
            <div className="footer-right">
              <button
                type="button"
                className="btn-modal-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Hủy
              </button>
              <button
                type="submit"
                className="btn-modal-primary"
                disabled={isSubmitting || !name.trim()}
              >
                <i className={isSubmitting ? "fas fa-spinner fa-spin" : (isEdit ? "fas fa-save" : "fas fa-user-plus")}></i>
                <span>{isEdit ? "Lưu thay đổi" : "Tạo nhân viên"}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
